import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import UsersList from 'components/UsersList/UsersList'
import { Tabs, Tab } from 'transfix-ui/components/DeprecatedTabs'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box, makeStyles } from 'transfix-ui/core'
import { ICarrierState, ICarrierFMCSAState } from 'types/carrier'
import { IUsersState } from 'types/user'
import { getCarrierDetails, getFMCSA } from 'utils/actions/carrier'
import { getUsersTotalCount } from 'utils/actions/users'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { CarrierProfileContext } from 'apps/carrier/context/CarrierProfileContext'
import { IStore } from 'apps/carrier/utils/reducers'
import {
  CARRIER_PROFILE_TABS,
  COMPLIANCE_TAB,
  SHIPMENTS_TAB,
  LOGS_TAB,
  MATCHING_SHIPMENTS_TAB,
  USERS_TAB,
  TRAILERS_TAB,
} from '../../constants/carrierProfile'
import { CarrierTrailers } from '../CarrierTrailers'
import { TrailerManagementContextProvider } from '../CarrierTrailers/TrailerManagementContext'
import { Compliance } from '../Compliance'
import { GeneralInformation } from '../GeneralInformation'
import Logs from '../Logs'
import MatchingShipmentsList from '../MatchingShipmentsList'
import Shipments from '../Shipments'
import CarrierProfileHeader from './CarrierProfileHeader'
import { useCarrierPageQuery } from './graphql/CarrierPage'
import { useMatchingShipmentsCountQuery } from './graphql/MatchingShipmentsCount'

interface ISelector {
  carrier: ICarrierState
  carrierFMCSA: ICarrierFMCSAState
  users: IUsersState
}

const useStyles = makeStyles({
  tabList: {
    '& ul': {
      padding: 0,
    },
    '& .tabs-active-indicator ': {
      left: 0,
    },
  },
})

export const Carrier = () => {
  const history = useHistory()
  const { id, tab = '' } = useParams<{ id: string; tab: string }>()
  const {
    carrier: { carrierDetails: carrierData },
    carrierFMCSA: { fmcsaInfo },
    users: { totalCount: usersTabCount },
  } = useSelector<IStore, ISelector>(({ carrier, carrierFMCSA, users }) => ({
    carrier,
    carrierFMCSA,
    users,
  }))

  const dispatch = useDispatch()

  const { getFlag } = useFeatureFlagContext()
  const { enabled: isMatchingShipmentsCardEnabled } = getFlag('displayMatchingShipmentsCard')
  const { enabled: isCarrierTrailerManagementEnabled } = getFlag('carrierTrailerManagement')

  const { data: carrierPageData, error, loading } = useCarrierPageQuery({
    variables: {
      id,
    },
  })

  const { carrierApplication } = carrierPageData ?? {}
  const { totalShipments: shipmentsCount } = carrierApplication ?? {}

  const { data } = useMatchingShipmentsCountQuery({
    variables: {
      carrierId: id,
    },
  })

  const matchingShipmentsCount = data?.shipmentListForCarrier.totalCount

  useEffect(() => {
    dispatch(getUsersTotalCount({ filters: { carrierId: id } }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    if (!carrierData && id) dispatch(getCarrierDetails(id))
  }, [carrierData])

  useEffect(() => {
    if (carrierData && !fmcsaInfo) {
      dispatch(getFMCSA(carrierData.id))
    }
  }, [carrierData, fmcsaInfo])

  const changeTab = (newTab: string) => {
    const path = CARRIER_PROFILE_TABS[newTab]
    history.push(path ? `/${id}/${path}` : `/${id}`)
  }

  const classes = useStyles()

  return (
    <CarrierProfileContext.Provider value={{ carrierApplication, error, loading }}>
      <TrailerManagementContextProvider carrierId={id}>
        <div className='container'>
          {carrierData ? (
            <>
              {carrierApplication && <CarrierProfileHeader carrierProfile={carrierApplication} />}
              <Box>
                <Tabs
                  dataTest='carrier-tabs'
                  value={tab || 'Carrier Overview'}
                  tabListClassName={classes.tabList}
                  onChange={changeTab}>
                  <Tab label='Carrier Overview'>
                    <GeneralInformation carrierApplication={carrierApplication} />
                  </Tab>
                  <Tab label='Compliance' value={COMPLIANCE_TAB}>
                    <Compliance />
                  </Tab>
                  <Tab label='Shipments' value={SHIPMENTS_TAB} count={shipmentsCount ?? undefined}>
                    <Shipments shipmentsCount={shipmentsCount ?? 0} />
                  </Tab>
                  {isCarrierTrailerManagementEnabled && (
                    <Tab label='Trailers' value={TRAILERS_TAB}>
                      <CarrierTrailers carrierId={id} />
                    </Tab>
                  )}
                  <Tab label='Users' value={USERS_TAB} count={usersTabCount}>
                    <UsersList
                      defaultCompanyType='carrier'
                      defaultApplicationId={Number(id)}
                      orgDotNumber={carrierData.dotNumber}
                      orgMcNumber={carrierData.mcNumber}
                      orgName={carrierData.legalName}
                    />
                  </Tab>
                  {isMatchingShipmentsCardEnabled && (
                    <Tab
                      label='Matching Shipments'
                      value={MATCHING_SHIPMENTS_TAB}
                      count={matchingShipmentsCount}>
                      <MatchingShipmentsList carrierId={carrierData.id} />
                    </Tab>
                  )}
                  <Tab label='Logs' value={LOGS_TAB}>
                    <Logs />
                  </Tab>
                </Tabs>
              </Box>
            </>
          ) : (
            <Spinner />
          )}
        </div>
      </TrailerManagementContextProvider>
    </CarrierProfileContext.Provider>
  )
}
